import { Typography } from '@mui/material'
import React, { ChangeEvent, memo, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SkipStep, UploadDOTNumber, UploadUSDOTNumber } from 'src/constants/actionTypes'
import { CollectDataStepIndex } from 'src/components/client/CollectDataStepIndex'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik, FormikProps } from 'formik'
import { DOTNumberSchema, USDOTNumberSchema } from './DOTNumberForm.validator'
import {
  DOTNumberFormValues,
  DOTNumberRequest,
  USDOTNumberRequest,
} from './DOTNumberForm.interface'
import { DOTFormType } from './DOTNumberForm.constants'

import { ShipmentMajor } from '@shopify/polaris-icons'
import { Button } from '../shared/Button'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { FormikRadioGroup } from '../shared/form/formik/FormikRadioGroup'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { USDOTNumberContent } from './USDOTNumberContent'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { states } from 'src/constants/states'
import { OptionalStepLabel } from '../client/OptionalStepLabel'
import { DOTNumberDetails } from './DOTNumberDetails'

const numberOptions = [
  { label: 'I want to submit DOT number', value: DOTFormType.DOT_NUMBER },
  { label: 'I want to submit USDOT number', value: DOTFormType.USDOT_NUMBER },
  { label: "I don't have a DOT/USDOT number", value: DOTFormType.NO_NUMBER },
]

const initialValues: DOTNumberFormValues = {
  file: undefined,
  dotNumber: '',
  stateOfIssue: '',
  usDotNumber: '',
  lastSubmittedAt: '',
  type: DOTFormType.DOT_NUMBER,
}

const getValidationSchema = (type: DOTFormType) => {
  const schemaMap = {
    [DOTFormType.DOT_NUMBER]: DOTNumberSchema,
    [DOTFormType.USDOT_NUMBER]: USDOTNumberSchema,
    [DOTFormType.NO_NUMBER]: undefined,
  }

  return schemaMap[type]
}

const DOTNumberFormComponent = () => {
  const dispatch = useDispatch()
  const currentStep = useSelector(getCurrentCollectDataStep)
  const formikRef = useRef<FormikProps<DOTNumberFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [currentFormType, setCurrentFormType] = useState<DOTFormType>(DOTFormType.DOT_NUMBER)
  const [isUSDOTNumberReadyForUpload, setIsUSDOTNumberReadyForUpload] = useState<boolean>(false)

  const handleFormSubmit = async (values: DOTNumberFormValues) => {
    if (!currentStep?.id) {
      return
    }

    setIsLoading(true)
    setError(null)

    let formattedDate = ''

    if (values.lastSubmittedAt) {
      const [month, day, year] = values.lastSubmittedAt.split('/')
      formattedDate = `${year}-${month}-${day}`
    }

    try {
      const currentType = (values.type as DOTFormType) || DOTFormType.DOT_NUMBER

      if (currentType === DOTFormType.DOT_NUMBER) {
        const requestBody: DOTNumberRequest = {
          dotNumber: values.dotNumber || '',
          stateOfIssue: values.stateOfIssue || '',
          stepId: currentStep.id,
          lastSubmittedAt: formattedDate,
        }

        if (values.file) {
          requestBody.file = values.file
        }

        await createAsyncAction(dispatch, UploadDOTNumber.request(requestBody))
      } else if (currentType === DOTFormType.USDOT_NUMBER) {
        const requestBody: USDOTNumberRequest = {
          usDotNumber: values.usDotNumber || '',
          stepId: currentStep.id,
          lastSubmittedAt: formattedDate,
        }

        if (values.file) {
          requestBody.file = values.file
        }

        await createAsyncAction(dispatch, UploadUSDOTNumber.request(requestBody))
      } else if (currentType === DOTFormType.NO_NUMBER) {
        const requestBody = {
          stepId: currentStep.id,
        }

        await createAsyncAction(dispatch, SkipStep.request(requestBody))
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as DOTFormType
    setCurrentFormType(newType)
    setIsUSDOTNumberReadyForUpload(false)

    if (formikRef.current) {
      formikRef.current.setFieldValue('type', newType)
    }
  }

  const radioOptions = useMemo(() => {
    if (currentStep?.optional) {
      return numberOptions
    }

    return numberOptions.filter((o) => o.value !== DOTFormType.NO_NUMBER)
  }, [currentStep?.optional])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={getValidationSchema(currentFormType)}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange, values }) => {
        const currentType = (values.type as DOTFormType) || DOTFormType.DOT_NUMBER

        return (
          <div css={tw`h-full w-full flex flex-col justify-between`}>
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <ShipmentMajor width={20} />
              </div>
              <div css={tw`flex items-center gap-2 mb-2`}>
                <CollectDataStepIndex stepId={currentStep?.id} />
                {currentStep?.optional && <OptionalStepLabel />}
              </div>
              <DOTNumberDetails />

              <div css={tw`mt-6`}>
                <Typography
                  css={tw`text-shades-400 text-xs font-bold uppercase`}
                  variant="overline"
                >
                  Select preferable option
                </Typography>

                <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                  <div css={tw`w-full mb-4`}>
                    <FormikRadioGroup
                      name="type"
                      options={radioOptions}
                      onChange={handleTypeChange}
                    />
                  </div>

                  {currentType === DOTFormType.DOT_NUMBER && (
                    <>
                      <div css={tw`mb-4`}>
                        <FormikTextField
                          name="dotNumber"
                          placeholder="Enter DOT Number"
                          label="DOT Number"
                          mask={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                          onChange={handleChange}
                          css={tw`w-full`}
                        />
                      </div>

                      <div css={tw`mb-4`}>
                        <FormikSelect
                          name="stateOfIssue"
                          label="State of issue"
                          placeholder="Select state"
                          options={states}
                          onChange={handleChange}
                        />
                      </div>

                      <div css={tw`mb-4`}>
                        <FormikTextField
                          name="lastSubmittedAt"
                          placeholder="Enter date"
                          label="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
                          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                          onChange={handleChange}
                          css={tw`w-full`}
                        />
                      </div>
                    </>
                  )}

                  {currentType === DOTFormType.USDOT_NUMBER && (
                    <USDOTNumberContent
                      handleChange={handleChange}
                      setIsUSDOTNumberReadyForUpload={setIsUSDOTNumberReadyForUpload}
                    />
                  )}

                  {(currentType === DOTFormType.DOT_NUMBER ||
                    (currentType === DOTFormType.USDOT_NUMBER && isUSDOTNumberReadyForUpload)) && (
                    <div css={tw`mt-6`}>
                      <Typography css={tw`mb-1.5 text-shades-400`}>
                        Upload DOT Medical Card
                      </Typography>
                      <FormikFileInput name="file" />
                    </div>
                  )}
                </div>
                {(currentType === DOTFormType.DOT_NUMBER ||
                  (currentType === DOTFormType.USDOT_NUMBER && isUSDOTNumberReadyForUpload) ||
                  (currentType === DOTFormType.NO_NUMBER && !!currentStep?.optional)) && (
                  <Button
                    className={tw`w-full mt-6`}
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                  >
                    Submit and Continue
                  </Button>
                )}
              </div>
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
          </div>
        )
      }}
    </Formik>
  )
}

export const DOTNumberForm = memo(DOTNumberFormComponent)
